import { Elements } from '@/components/elements';
import clsx from 'clsx';
import PitchTagIcon from '@icons/ThePitch.svg';
import Image from 'next/image';

export type TheDefenseProps = {
    titleSuffix?: string;
    description?: string;
    pitchTag?: string;
    quote?: string;
    className?: string;
    panelClassName?: string;
    quoteBlockClassName?: string;
};

const DEFAULT_TITLE_SUFFIX =
    'Use Case: Fighting "Vendor Consolidation"';

const DEFAULT_DESCRIPTION =
    'Employers in 2026 are tired of "app fatigue." They want one login for all their pre-tax accounts';

const DEFAULT_PITCH_TAG = 'The Pitch';

const DEFAULT_QUOTE = `"Mr. Employer, you already trust us with your employees' HSA and FSA data. By using our ICHRA platform, you keep all your healthcare 'dollars' in one secure vault, with one set of reports and one customer service team. It's the only way to ensure your ICHRA and HSA work together without violating IRS 'double-dipping' rules."`;

const HIGHLIGHT_PHRASE = '"Defense"';

export function TheDefense({
    titleSuffix = DEFAULT_TITLE_SUFFIX,
    description = DEFAULT_DESCRIPTION,
    pitchTag = DEFAULT_PITCH_TAG,
    quote = DEFAULT_QUOTE,
    className,
    panelClassName,
    quoteBlockClassName,
}: TheDefenseProps) {
    const fullTitle = `The ${HIGHLIGHT_PHRASE} ${titleSuffix}`;

    return (
        <section
            className={clsx(
                'overflow-hidden px-5 py-16 md:px-10 md:py-16 lg:px-[79px] lg:py-20',
                className,
            )}
        >
            <div
                className={clsx(
                    'mx-auto w-full max-w-[1200px] rounded-2xl bg-brand-50 p-6 md:p-10 lg:p-8',
                    panelClassName,
                )}
            >
                <div className="grid grid-cols-1 items-start gap-8 lg:grid-cols-2 lg:gap-12">
                    <div className="flex flex-col gap-4 lg:pt-1">
                        <Elements.Title
                            title={fullTitle}
                            type="h2"
                            highlights={[
                                {
                                    text: HIGHLIGHT_PHRASE,
                                    className: 'text-brand-700',
                                },
                            ]}
                            className="text-3xl font-semibold text-gray-900 md:text-[32px] lg:text-4xl"
                        />
                        <Elements.Description
                            description={description}
                            type="medium"
                            className="text-gray-800 md:text-lg"
                        />
                    </div>
                    <div
                        className={clsx(
                            'flex flex-col gap-4 rounded-2xl border-brand-200 bg-gray-50 shadow-[0px_0px_5.8px_0px_#0000001F_inset] p-5 md:p-6',
                            quoteBlockClassName,
                        )}
                    >
                        <span
                            className={clsx(
                                'inline-flex gap-2 w-fit rounded-lg bg-brand-100 px-2 py-1 text-base font-medium text-brand-700',
                            )}
                        >
                            <Image src={PitchTagIcon} alt={pitchTag} width={20} height={20} />
                            {pitchTag}
                        </span>
                        <blockquote className="m-0 text-base leading-snug font-normal text-gray-800 italic md:text-lg">
                            {quote}
                        </blockquote>
                    </div>
                </div>
            </div>
        </section>
    );
}
